import { createFileRoute, redirect } from '@tanstack/react-router';
import { z } from 'zod';
import { loadUser } from '@/state/session';
import { dictionariesQuery, queryClient } from '@/state/queries';
import { db } from '@/db/db';
import { storage } from '@/lib/storage';

// Landing hand-off: app.solungo.com?locale=ru&target=tr prefills onboarding (docs/project.md §10)
const searchSchema = z.object({ locale: z.string().optional(), target: z.string().optional() });

export const Route = createFileRoute('/')({
  validateSearch: searchSchema,
  beforeLoad: async ({ search }) => {
    if (search.locale) storage.setPendingLocale(search.locale);
    if (search.target) storage.setPendingTarget(search.target);
    const user = await loadUser();
    if (!user) throw redirect({ to: '/auth', replace: true });
    // The Dexie copy of the list first: a device that has one redirects at once, since a network
    // that is down while navigator.onLine stays true (no signal, Wi-Fi without internet) would
    // otherwise hold the start; Home refreshes the list behind it. Only a device with nothing
    // stored (the first start after sign-in) waits for the API.
    let dictionaries = await db.dictionaries.toArray();
    if (dictionaries.length === 0 && navigator.onLine) {
      try {
        dictionaries = await queryClient.ensureQueryData(dictionariesQuery);
      } catch {
        // the API is unreachable: nothing to show but onboarding or a new dictionary
      }
    }
    // A brand-new user goes through onboarding; an invited teacher (no name, a shared dictionary)
    // skips it (docs/project.md §4)
    if (user.name === null && dictionaries.length === 0) throw redirect({ to: '/onboarding', replace: true });
    if (dictionaries.length === 0) throw redirect({ to: '/dictionaries/new', replace: true });
    const last = storage.getLastDictionary();
    const target = dictionaries.find((d) => d.id === last) ?? dictionaries[0]!;
    throw redirect({ to: '/d/$dictionaryId', params: { dictionaryId: target.id }, replace: true });
  },
  component: () => null,
});
